// Mini-tutorial de 3 toques no Dashboard.
//
// Persiste a flag em localStorage `cm_dashboard_tour_v1`. Aparece somente
// uma vez na vida do device. Permite "pular tudo" a qualquer momento.
import React, { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";

const KEY = "cm_dashboard_tour_v1";

const STEPS = [
  {
    selector: "dashboard-card-continue",
    title: "Continue de onde parou",
    body: "Toque aqui sempre que quiser retomar sua leitura do último capítulo.",
  },
  {
    selector: "dashboard-card-journey",
    title: "Minha Jornada",
    body: "Aqui você registra seu dia: oração, leitura, família, saúde e propósito.",
  },
  {
    selector: "dashboard-card-assistant",
    title: "Auxiliar Bíblico",
    body: "Tire dúvidas e receba reflexões personalizadas, fala com você de verdade.",
  },
];

function getDone() {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return true;
  }
}
function setDone() {
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    /* ignore */
  }
}

export default function DashboardTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [box, setBox] = useState(null);

  useEffect(() => {
    if (getDone()) return;
    // Espera o dashboard montar.
    const t = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = document.querySelector(
      `[data-testid="${STEPS[step].selector}"]`,
    );
    if (!el) {
      setBox(null);
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const recalc = () => {
      const r = el.getBoundingClientRect();
      setBox({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
      });
    };
    recalc();
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, { passive: true });
    return () => {
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc);
    };
  }, [open, step]);

  const close = (markDone = true) => {
    if (markDone) setDone();
    setOpen(false);
  };

  const next = () => {
    if (step >= STEPS.length - 1) return close(true);
    setStep(step + 1);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60]"
      data-testid="dashboard-tour"
      role="dialog"
      aria-label="Tutorial do painel"
    >
      {/* Dim overlay, clicks anywhere skip the tour */}
      <button
        type="button"
        onClick={() => close(true)}
        data-testid="dashboard-tour-skip"
        aria-label="Pular tutorial"
        className="absolute inset-0 bg-navy/40 backdrop-blur-[1px] cursor-pointer"
      />

      {/* Spotlight ring around target */}
      {box && (
        <div
          aria-hidden
          className="absolute rounded-2xl ring-2 ring-gold ring-offset-2 ring-offset-transparent pointer-events-none transition-all duration-300"
          style={{
            top: box.top - 4,
            left: box.left - 4,
            width: box.width + 8,
            height: box.height + 8,
            boxShadow: "0 0 0 9999px rgba(11, 30, 63, 0.45)",
          }}
        />
      )}

      {/* Speech bubble, posicionado abaixo do alvo (ou centro se não houver) */}
      <div
        data-testid="dashboard-tour-bubble"
        className="absolute left-1/2 -translate-x-1/2 w-[90%] max-w-sm rounded-2xl bg-white shadow-2xl p-5"
        style={{
          top: box
            ? Math.min(box.top + box.height + 16, window.innerHeight - 220)
            : "50%",
        }}
      >
        <button
          type="button"
          onClick={() => close(true)}
          aria-label="Fechar"
          className="absolute right-3 top-3 w-7 h-7 rounded-full hover:bg-navy/5 flex items-center justify-center"
        >
          <X className="w-4 h-4 text-navy/55" />
        </button>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
          Passo {step + 1} de {STEPS.length}
        </p>
        <p className="font-display text-xl text-navy mt-1.5">
          {STEPS[step].title}
        </p>
        <p className="text-sm text-navy/70 mt-2 leading-relaxed">
          {STEPS[step].body}
        </p>
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => close(true)}
            data-testid="dashboard-tour-skip-text"
            className="text-[11px] uppercase tracking-widest text-navy/55 hover:text-navy"
          >
            Pular
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="dashboard-tour-next"
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest"
          >
            {step >= STEPS.length - 1 ? "Concluir" : "Próximo"}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
